import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, MessageCircle, LineChart, Users, CalendarHeart, ShieldAlert, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Serenity — Mental wellness, anonymous and always with you" },
      { name: "description", content: "Anonymous counseling, AI companion Sage, mood tracking, therapist booking, and 24/7 crisis support. Break the stigma. Start healing." },
      { property: "og:title", content: "Serenity — Mental wellness, always with you" },
      { property: "og:description", content: "Anonymous AI counseling, mood tracking, therapist booking and crisis support." },
    ],
  }),
  component: Landing,
});

const features = [
  { icon: MessageCircle, title: "Sage — your AI companion", desc: "Talk to a warm, judgment-free AI 24/7. Trained to listen, validate, and guide grounding exercises." },
  { icon: Users, title: "Anonymous counseling", desc: "Sign up with a random handle. No real names, no judgment — just space to be heard." },
  { icon: LineChart, title: "Mood tracker", desc: "Log how you feel in seconds. Watch patterns emerge over weeks so you understand yourself." },
  { icon: CalendarHeart, title: "Licensed therapists", desc: "Browse verified clinicians and book sessions when you're ready for human-led care." },
  { icon: ShieldAlert, title: "Crisis intervention", desc: "One tap connects you to lifelines worldwide. Grounding techniques the moment you need them." },
  { icon: Sparkles, title: "Truly private", desc: "Conversations are encrypted, anonymous, and yours. We never sell data. Ever." },
];

function Landing() {
  return (
    <div className="relative">
      <header className="px-6 py-6 max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary to-accent glow-primary" />
          <span className="font-display text-2xl">Serenity</span>
        </div>
        <Link to="/auth"><Button variant="ghost">Sign in</Button></Link>
      </header>

      <section className="px-6 pt-16 pb-32 max-w-6xl mx-auto">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs text-muted-foreground">
            <Heart className="h-3 w-3 text-accent" />
            Free, anonymous, and always with you
          </div>
          <h1 className="text-5xl md:text-7xl text-gradient leading-[1.05]">
            Mental health care<br />without the wait, or the stigma.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            With one psychiatrist for every 100,000 people in many regions, getting help is hard.
            Serenity brings AI counseling, anonymous support, mood tracking, and licensed therapists into one quiet place.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/auth"><Button size="lg" className="rounded-full px-8 glow-primary">Start anonymously — it's free</Button></Link>
            <Link to="/crisis"><Button size="lg" variant="ghost" className="rounded-full">In crisis right now?</Button></Link>
          </div>
        </div>
      </section>

      <section className="px-6 pb-32 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="glass rounded-3xl p-6 space-y-3 hover:border-primary/30 transition">
              <div className="h-10 w-10 rounded-2xl bg-secondary/60 flex items-center justify-center">
                <f.icon className="h-5 w-5 text-accent" />
              </div>
              <h3 className="text-xl">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-32 max-w-3xl mx-auto text-center space-y-6">
        <h2 className="text-4xl text-gradient">Healing in your pocket.</h2>
        <p className="text-muted-foreground">Join thousands quietly choosing themselves.</p>
        <Link to="/auth"><Button size="lg" className="rounded-full px-8 glow-primary">Create your safe space</Button></Link>
      </section>

      <footer className="border-t border-border/40 px-6 py-8 text-center text-xs text-muted-foreground">
        Serenity is not a substitute for emergency care. In crisis, call your local emergency number or visit <Link to="/crisis" className="underline">our crisis page</Link>.
      </footer>
    </div>
  );
}
